import {
  setIsAccessDenied,
  setIsInitialized,
  setIsInitializing,
  setIsNoCompanyFound,
  setIsNoPersonResolved,
} from '@store/features/loading';
import {
  selectActiveCompany,
  selectActiveCompanyId,
  selectCompanies,
  selectFundingApplications,
} from '@features/workspace/store/selectors';
import {
  setActiveCompanyId,
  setIsBankAccountsLoading,
  setIsDocumentsLoading,
  setPersonId,
} from '@features/workspace/store';
import {
  fetchAdvance,
  fetchBankAccounts,
  fetchCompanies,
  fetchCompanyUsers,
  fetchCompanyDocuments,
  fetchCompanyFundingApplications,
} from '@features/workspace/store/actions/fetch';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { useEffect } from 'react';
import { IAppDispatch, IRootState } from '@app/store';
import logger from '@/app/services/logger';

const fetchCompanyRelatedData = async (
  dispatch: IAppDispatch,
  getState: () => IRootState
) => {
  // Control outside given that docs collection combines company and users documents from different thunks
  const activeCompany = selectActiveCompany(getState());
  // Fetch company users as employees

  dispatch(setIsDocumentsLoading(true));
  Promise.all([
    dispatch(fetchCompanyDocuments(null)),
    dispatch(fetchCompanyUsers(null)),
  ]).then(() => {
    dispatch(setIsDocumentsLoading(false));
  });

  if (activeCompany) {
    // BANKING
    // Fetch bank accounts if linked to Traderoot
    if (
      activeCompany?.traderootMemberId &&
      activeCompany?.traderootMemberId !==
        '00000000-0000-0000-0000-000000000000'
    ) {
      // ASYNC BANKING
      dispatch(fetchBankAccounts(null));
    } else {
      dispatch(setIsBankAccountsLoading(false));
    }

    // CREDIT
    // Fetch funding applications if linked to Credit
    if (activeCompany?.creditApiCompanyId) {
      dispatch(fetchCompanyFundingApplications(null)).then(() => {
        const fundingApplications = selectFundingApplications(getState());

        if (fundingApplications?.length) {
          fundingApplications
            .filter((fundingApp: any) => fundingApp.advanceId)
            .map((fundingApp: any) =>
              dispatch(fetchAdvance(fundingApp.advanceId))
            );
        }
      });
    }
  }
};

export const initFetchByPerson =
  (personId: string) =>
  async (dispatch: IAppDispatch, getState: () => IRootState) => {
    // can't resolve person id
    if (!personId || personId === 'undefined') {
      dispatch(setIsInitializing(false));
      dispatch(setIsNoPersonResolved(true));

      return;
    }
    // Setup source person
    dispatch(setPersonId(personId));
    // Clear the outcome of any earlier attempt, e.g. one made before the agent
    // had to sign in again
    dispatch(setIsNoPersonResolved(false));
    dispatch(setIsNoCompanyFound(false));
    dispatch(setIsAccessDenied(false));
    // Fetch person companies
    try {
      await dispatch(fetchCompanies(personId)).unwrap();
    } catch (err) {
      const status = (err as { status?: number })?.status;

      logger.error(
        new Error(`fetchCompanies failed with status ${status ?? 'unknown'}`)
      );
      dispatch(setIsInitializing(false));
      if (status === 403) {
        dispatch(setIsAccessDenied(true));
      } else if (status !== 401) {
        // A 401 shows the Sign In screen instead (see axios.ts), and the lookup
        // runs again once the agent has signed in
        dispatch(setIsNoCompanyFound(true));
      }

      return;
    }
    // Setup first user company as current
    const companies = selectCompanies(getState());

    if (!companies.length) {
      logger.error(new Error('No companies assigned to user'));
      dispatch(setIsInitializing(false));
      dispatch(setIsNoCompanyFound(true));

      return;
    }
    // Setup active company if not set yet
    const activeCompanyId = selectActiveCompanyId(getState());

    if (!activeCompanyId && companies.length) {
      dispatch(setActiveCompanyId(companies[0].id));
    }

    dispatch(setIsInitializing(false));
    dispatch(setIsInitialized(true));
  };

export const useCompanyWatcher = () => {
  const dispatch = useAppDispatch();
  const activeCompanyId = useAppSelector(selectActiveCompanyId);
  const companies = useAppSelector(selectCompanies);

  useEffect(() => {
    if (activeCompanyId && companies.length) {
      dispatch(fetchCompanyRelatedData);
    }
  }, [dispatch, activeCompanyId, companies]);
};
